'use client';

import { motion, useReducedMotion } from 'motion/react';

import { cn } from '@/lib/utils';

export type RevealDirection = 'up' | 'fade' | 'left';

export type MotionRevealProps = {
  children: React.ReactNode;
  direction?: RevealDirection;
  delay?: number;
  duration?: number;
  once?: boolean;
  className?: string;
};

/**
 * Orientation: subtle viewport entrance for section content.
 * Motion justification: coordinated spring whileInView cannot be expressed
 * reliably in CSS without hiding pre-hydration content.
 * Reduced motion: static render, no transform/opacity gating.
 */
export function MotionReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.55,
  once = true,
  className,
}: MotionRevealProps) {
  const reducedMotion = useReducedMotion() ?? false;

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const hidden =
    direction === 'fade'
      ? { opacity: 0 }
      : direction === 'left'
        ? { opacity: 0, x: -20 }
        : { opacity: 0, y: 20 };

  return (
    <motion.div
      className={cn(className)}
      initial={hidden}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once, margin: '-8%' }}
      transition={{
        type: 'spring',
        bounce: 0,
        duration,
        delay,
      }}
    >
      {children}
    </motion.div>
  );
}
